import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '@/contexts/BusinessContext';
import { useAuth } from '@/contexts/AuthContext';
import { updateBusiness, updatePin, deleteBusiness } from '@/lib/api';
import { encryptStripeKey } from '@/lib/encryption';
import { Settings, Building2, CreditCard as Edit2, Info, Lock, LogOut, X, Trash2, KeyRound, UserX } from 'lucide-react';
import LogoDropzone from '@/components/LogoDropzone';
import LogoSpinner from '@/components/LogoSpinner';
import ChangePasswordModal from './settings/ChangePasswordModal';
import MfaSettingsSection from './settings/MfaSettingsSection';
import TrustedDevicesSection from './settings/TrustedDevicesSection';
import BiometricSettingsSection from './settings/BiometricSettingsSection';
import IntegrationsSection from './settings/IntegrationsSection';
import NotificationsSection from './settings/NotificationsSection';
import DataManagementSection from './settings/DataManagementSection';
import PreferencesSection from './settings/PreferencesSection';
import DeleteAccountModal from './settings/DeleteAccountModal';

export default function SettingsPage() {
  const { selectedBusiness, setSelectedBusiness } = useBusiness();
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const [rateModalVisible, setRateModalVisible] = useState(false);
  const [pinModalVisible, setPinModalVisible] = useState(false);
  const [editBusinessModalVisible, setEditBusinessModalVisible] = useState(false);
  const [deleteConfirmModalVisible, setDeleteConfirmModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [deleteAccountModalVisible, setDeleteAccountModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bankRate, setBankRate] = useState('15.42');
  const [creditCardRate, setCreditCardRate] = useState('15.42');
  const [defaultBlackMarketRate, setDefaultBlackMarketRate] = useState('20.25');
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [editFormData, setEditFormData] = useState({ name: '', stripeKey: '', bankRate: '', creditCardRate: '', blackMarketRate: '', logoUrl: '' });

  useEffect(() => {
    if (!selectedBusiness) { navigate('/businesses', { replace: true }); return; }
    setBankRate(selectedBusiness.bank_rate.toString());
    setCreditCardRate(selectedBusiness.credit_card_rate.toString());
    setDefaultBlackMarketRate(selectedBusiness.default_black_market_rate.toString());
  }, [selectedBusiness, navigate]);

  async function handleSaveRates() {
    if (!selectedBusiness) return;
    const newBankRate = parseFloat(bankRate);
    const newCreditCardRate = parseFloat(creditCardRate);
    const newBlackMarketRate = parseFloat(defaultBlackMarketRate);
    if (isNaN(newBankRate) || newBankRate <= 0) { alert('Please enter a valid bank rate'); return; }
    if (isNaN(newCreditCardRate) || newCreditCardRate <= 0) { alert('Please enter a valid credit card rate'); return; }
    if (isNaN(newBlackMarketRate) || newBlackMarketRate <= 0) { alert('Please enter a valid black market rate'); return; }
    try {
      setLoading(true);
      const updated = await updateBusiness(selectedBusiness.id, { bank_rate: newBankRate, credit_card_rate: newCreditCardRate, default_black_market_rate: newBlackMarketRate });
      setSelectedBusiness(updated);
      setRateModalVisible(false);
    } catch (error: any) {
      alert(error.message || 'Failed to update rates');
    } finally { setLoading(false); }
  }

  function handleSwitchBusiness() { setSelectedBusiness(null); navigate('/businesses'); }

  async function handleChangePin() {
    if (!currentPin || !newPin || !confirmPin) { alert('Please fill in all PIN fields'); return; }
    if (currentPin.length !== 4 || newPin.length !== 4 || confirmPin.length !== 4) { alert('PIN must be exactly 4 digits'); return; }
    if (!/^\d+$/.test(currentPin) || !/^\d+$/.test(newPin) || !/^\d+$/.test(confirmPin)) { alert('PIN must contain only numbers'); return; }
    if (newPin !== confirmPin) { alert('New PIN and confirmation do not match'); return; }
    if (currentPin === newPin) { alert('New PIN must be different from current PIN'); return; }
    try {
      setLoading(true);
      await updatePin(currentPin, newPin);
      setPinModalVisible(false);
      setCurrentPin(''); setNewPin(''); setConfirmPin('');
      alert('PIN changed successfully');
    } catch (error: any) {
      alert(error.message || 'Failed to change PIN');
    } finally { setLoading(false); }
  }

  function openEditBusinessModal() {
    if (!selectedBusiness) return;
    setEditFormData({ name: selectedBusiness.name, stripeKey: '', bankRate: selectedBusiness.bank_rate.toString(), creditCardRate: selectedBusiness.credit_card_rate.toString(), blackMarketRate: selectedBusiness.default_black_market_rate.toString(), logoUrl: selectedBusiness.logo_url || '' });
    setEditBusinessModalVisible(true);
  }

  async function handleSaveBusinessEdit() {
    if (!selectedBusiness) return;
    if (!editFormData.name.trim()) { alert('Please enter a business name'); return; }
    try {
      setLoading(true);
      const updates: any = { name: editFormData.name, bank_rate: parseFloat(editFormData.bankRate), credit_card_rate: parseFloat(editFormData.creditCardRate), default_black_market_rate: parseFloat(editFormData.blackMarketRate), logo_url: editFormData.logoUrl || null };
      if (editFormData.stripeKey.trim()) {
        updates.stripe_key_encrypted = await encryptStripeKey(selectedBusiness.id, editFormData.stripeKey);
      }
      const updated = await updateBusiness(selectedBusiness.id, updates);
      setSelectedBusiness(updated);
      setEditBusinessModalVisible(false);
      alert('Business updated successfully');
    } catch (error: any) {
      alert(error.message || 'Failed to update business');
    } finally { setLoading(false); }
  }

  async function confirmDeleteBusiness() {
    if (!selectedBusiness) return;
    try {
      setLoading(true);
      setDeleteConfirmModalVisible(false);
      await deleteBusiness(selectedBusiness.id);
      setSelectedBusiness(null);
      navigate('/businesses');
    } catch (error: any) {
      alert(error.message || 'Failed to delete business');
    } finally { setLoading(false); }
  }

  async function handleSignOut() {
    if (window.confirm('Are you sure you want to sign out?')) {
      setSelectedBusiness(null);
      await signOut();
    }
  }

  if (!selectedBusiness) {
    return (
      <div className="h-full flex items-center justify-center">
        <LogoSpinner size={48} />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="glass-header shrink-0 px-4 sm:px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-teal-500/10 flex items-center justify-center shrink-0">
            <Settings size={18} className="text-teal-400" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">Settings</h1>
            <p className="text-xs text-white">Manage your business configuration</p>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-auto p-4 sm:p-5">
        <div className="max-w-3xl mx-auto space-y-5">

          <div>
            <h2 className="text-[11px] font-semibold text-white uppercase tracking-wider mb-2.5 px-1">Business</h2>
            <div className="glass-panel rounded-2xl p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-teal-500/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {selectedBusiness.logo_url ? (
                    <img src={selectedBusiness.logo_url} alt="Logo" className="w-9 h-9 object-cover" />
                  ) : (
                    <Building2 size={18} className="text-teal-400" />
                  )}
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-bold text-white truncate">{selectedBusiness.name}</h3>
                  <p className="text-[11px] text-white">Current Business</p>
                </div>
              </div>
              <button
                onClick={handleSwitchBusiness}
                className="w-full py-2.5 px-4 glass-button-active text-white rounded-xl text-xs font-semibold mb-4"
              >
                Switch Business
              </button>
              <div className="h-px bg-white/10 mb-4"></div>
              <div className="flex gap-2.5">
                <button
                  onClick={openEditBusinessModal}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-4 glass-inner text-teal-400 rounded-xl glass-hover-teal text-xs font-semibold border border-white/10"
                >
                  <Edit2 size={13} /> Edit
                </button>
                <button
                  onClick={() => setDeleteConfirmModalVisible(true)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-4 bg-red-500/10 text-red-600 rounded-xl glass-hover-red text-xs font-semibold"
                >
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-[11px] font-semibold text-white uppercase tracking-wider mb-2.5 px-1">Exchange Rates</h2>
            <div className="glass-panel rounded-2xl">
              <button
                onClick={() => setRateModalVisible(true)}
                className="w-full glass-hover rounded-2xl p-5 text-left"
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-[11px] text-white font-medium mb-1">Debit Bank Rate</p>
                    <p className="text-xl font-bold text-white">{selectedBusiness.bank_rate.toFixed(4)}</p>
                  </div>
                  <span className="text-[10px] text-teal-400 font-semibold bg-teal-500/10 px-2 py-1 rounded-lg">Click to edit</span>
                </div>
                <div className="h-px bg-white/10 my-3"></div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-[11px] text-white font-medium mb-1">Credit Card Rate</p>
                    <p className="text-xl font-bold text-white">{selectedBusiness.credit_card_rate.toFixed(4)}</p>
                  </div>
                </div>
                <div className="h-px bg-white/10 my-3"></div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px] text-white font-medium mb-1">Black Market Rate</p>
                    <p className="text-xl font-bold text-white">{selectedBusiness.default_black_market_rate.toFixed(4)}</p>
                  </div>
                </div>
              </button>
            </div>
          </div>

          <div>
            <h2 className="text-[11px] font-semibold text-white uppercase tracking-wider mb-2.5 px-1">Security</h2>
            <div className="glass-panel rounded-2xl p-2">
              <button
                onClick={() => setPinModalVisible(true)}
                className="w-full flex items-center gap-3 py-2.5 glass-hover rounded-xl px-3"
              >
                <div className="w-9 h-9 rounded-xl bg-teal-500/10 flex items-center justify-center flex-shrink-0">
                  <Lock size={15} className="text-teal-400" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-semibold text-white">Change PIN</p>
                  <p className="text-[11px] text-white">Update your 4-digit PIN</p>
                </div>
              </button>
              <div className="h-px bg-white/10 mx-3 my-1"></div>
              <button
                onClick={() => setPasswordModalVisible(true)}
                className="w-full flex items-center gap-3 py-2.5 glass-hover rounded-xl px-3"
              >
                <div className="w-9 h-9 rounded-xl bg-teal-500/10 flex items-center justify-center flex-shrink-0">
                  <KeyRound size={15} className="text-teal-400" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-semibold text-white">Change Password</p>
                  <p className="text-[11px] text-white">Update your login password</p>
                </div>
              </button>
              <div className="h-px bg-white/10 mx-3 my-1"></div>
              <MfaSettingsSection />
              <div className="h-px bg-white/10 mx-3 my-1"></div>
              <TrustedDevicesSection />
              <div className="h-px bg-white/10 mx-3 my-1"></div>
              <BiometricSettingsSection />
              <div className="h-px bg-white/10 mx-3 my-1"></div>
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-3 py-2.5 glass-hover-red rounded-xl px-3"
              >
                <div className="w-9 h-9 rounded-xl bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <LogOut size={15} className="text-red-500" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-semibold text-red-500">Sign Out</p>
                  <p className="text-[11px] text-white">Log out of your account</p>
                </div>
              </button>
            </div>
          </div>

          <div>
            <h2 className="text-[11px] font-semibold text-white uppercase tracking-wider mb-2.5 px-1">About</h2>
            <div className="glass-panel rounded-2xl p-5">
              <div className="flex justify-between items-center py-1.5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-teal-500/10 flex items-center justify-center flex-shrink-0">
                    <Info size={15} className="text-teal-400" />
                  </div>
                  <p className="text-sm text-white">App Name</p>
                </div>
                <p className="text-sm font-semibold text-white">X Manage</p>
              </div>
              <div className="h-px bg-white/10 my-3"></div>
              <div className="flex justify-between items-center py-1.5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-teal-500/10 flex items-center justify-center flex-shrink-0">
                    <Info size={15} className="text-teal-400" />
                  </div>
                  <p className="text-sm text-white">Version</p>
                </div>
                <p className="text-sm font-semibold text-white">1.0.0</p>
              </div>
            </div>
          </div>

          <IntegrationsSection onReconnect={openEditBusinessModal} />
          <NotificationsSection />
          <DataManagementSection />
          <PreferencesSection />

          <div>
            <h2 className="text-[11px] font-semibold text-white uppercase tracking-wider mb-2.5 px-1">Account</h2>
            <div className="glass-panel rounded-2xl p-2">
              <button
                onClick={() => setDeleteAccountModalVisible(true)}
                className="w-full flex items-center gap-3 py-2.5 glass-hover-red rounded-xl px-3"
              >
                <div className="w-9 h-9 rounded-xl bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <UserX size={15} className="text-red-500" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-semibold text-red-500">Delete Account</p>
                  <p className="text-[11px] text-white">Permanently remove your account and all data</p>
                </div>
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Rate Modal */}
      {rateModalVisible && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center p-4 z-50">
          <div className="glass-modal rounded-2xl p-6 w-full max-w-sm ">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-base font-bold text-white">Edit Exchange Rates</h2>
              <button onClick={() => setRateModalVisible(false)} className="p-1.5 glass-hover rounded-xl">
                <X size={18} className="text-white" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-white mb-1.5">Debit Bank Rate (MVR)</label>
                <input
                  type="number"
                  step="0.0001"
                  value={bankRate}
                  onChange={(e) => setBankRate(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/10 text-white rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-white mb-1.5">Credit Card Rate (MVR)</label>
                <input
                  type="number"
                  step="0.0001"
                  value={creditCardRate}
                  onChange={(e) => setCreditCardRate(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/10 text-white rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-white mb-1.5">Default Black Market Rate (MVR)</label>
                <input
                  type="number"
                  step="0.0001"
                  value={defaultBlackMarketRate}
                  onChange={(e) => setDefaultBlackMarketRate(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/10 text-white rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200"
                />
              </div>
              <p className="text-[11px] text-white">These rates are used for profit calculations. Debit and credit card transactions use separate bank rates.</p>
            </div>
            <div className="flex gap-2.5 mt-5">
              <button
                onClick={() => setRateModalVisible(false)}
                className="flex-1 px-4 py-2.5 text-sm glass-button text-white rounded-xl font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveRates}
                disabled={loading}
                className="flex-1 px-4 py-2.5 text-sm glass-button-active text-white rounded-xl font-medium disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PIN Modal */}
      {pinModalVisible && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center p-4 z-50">
          <div className="glass-modal rounded-2xl p-6 w-full max-w-sm ">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-base font-bold text-white">Change PIN</h2>
              <button onClick={() => { setPinModalVisible(false); setCurrentPin(''); setNewPin(''); setConfirmPin(''); }} className="p-1.5 glass-hover rounded-xl">
                <X size={18} className="text-white" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-white mb-1.5">Current PIN</label>
                <input
                  type="password"
                  inputMode="numeric"
                  value={currentPin}
                  onChange={(e) => setCurrentPin(e.target.value)}
                  maxLength={4}
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/10 text-white rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200"
                  placeholder="Current PIN"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-white mb-1.5">New PIN</label>
                <input
                  type="password"
                  inputMode="numeric"
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value)}
                  maxLength={4}
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/10 text-white rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200"
                  placeholder="New 4-digit PIN"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-white mb-1.5">Confirm New PIN</label>
                <input
                  type="password"
                  inputMode="numeric"
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value)}
                  maxLength={4}
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/10 text-white rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200"
                  placeholder="Confirm PIN"
                />
              </div>
            </div>
            <div className="flex gap-2.5 mt-5">
              <button
                onClick={() => { setPinModalVisible(false); setCurrentPin(''); setNewPin(''); setConfirmPin(''); }}
                className="flex-1 px-4 py-2.5 text-sm glass-button text-white rounded-xl font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleChangePin}
                disabled={loading}
                className="flex-1 px-4 py-2.5 text-sm glass-button-active text-white rounded-xl font-medium disabled:opacity-50"
              >
                {loading ? 'Changing...' : 'Change PIN'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Business Modal */}
      {editBusinessModalVisible && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center p-4 z-50">
          <div className="glass-modal rounded-2xl p-6 w-full max-w-md max-h-[80vh] overflow-y-auto ">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-base font-bold text-white">Edit Business</h2>
              <button onClick={() => setEditBusinessModalVisible(false)} className="p-1.5 glass-hover rounded-xl">
                <X size={18} className="text-white" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-white mb-1.5">Business Name</label>
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/10 text-white rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-white mb-1.5">Logo</label>
                <LogoDropzone
                  value={editFormData.logoUrl}
                  onChange={(dataUrl) => setEditFormData({ ...editFormData, logoUrl: dataUrl })}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-white mb-1.5">Stripe Key (leave empty to keep current)</label>
                <input
                  type="password"
                  value={editFormData.stripeKey}
                  onChange={(e) => setEditFormData({ ...editFormData, stripeKey: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/10 text-white rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200"
                  placeholder="sk_test_..."
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-white mb-1.5">Debit Bank Rate (MVR)</label>
                <input
                  type="number"
                  step="0.0001"
                  value={editFormData.bankRate}
                  onChange={(e) => setEditFormData({ ...editFormData, bankRate: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/10 text-white rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-white mb-1.5">Credit Card Rate (MVR)</label>
                <input
                  type="number"
                  step="0.0001"
                  value={editFormData.creditCardRate}
                  onChange={(e) => setEditFormData({ ...editFormData, creditCardRate: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/10 text-white rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-white mb-1.5">Default Black Market Rate (MVR)</label>
                <input
                  type="number"
                  step="0.0001"
                  value={editFormData.blackMarketRate}
                  onChange={(e) => setEditFormData({ ...editFormData, blackMarketRate: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/10 text-white rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200"
                />
              </div>
            </div>
            <div className="flex gap-2.5 mt-5">
              <button
                onClick={() => setEditBusinessModalVisible(false)}
                className="flex-1 px-4 py-2.5 text-sm glass-button text-white rounded-xl font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveBusinessEdit}
                disabled={loading}
                className="flex-1 px-4 py-2.5 text-sm glass-button-active text-white rounded-xl font-medium disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirmModalVisible && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center p-4 z-50">
          <div className="glass-modal rounded-2xl p-6 w-full max-w-sm ">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-2xl bg-red-500/10 flex items-center justify-center">
                <Trash2 size={22} className="text-red-500" />
              </div>
            </div>
            <h2 className="text-base font-bold text-white mb-2 text-center">Delete Business</h2>
            <p className="text-sm text-white text-center mb-5">This action cannot be undone.</p>
            <div className="flex gap-2.5">
              <button
                onClick={() => setDeleteConfirmModalVisible(false)}
                className="flex-1 px-4 py-2.5 text-sm glass-button text-white rounded-xl font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteBusiness}
                disabled={loading}
                className="flex-1 px-4 py-2.5 text-sm bg-red-500/10 text-red-600 rounded-xl glass-hover-red font-medium disabled:opacity-50"
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ChangePasswordModal visible={passwordModalVisible} onClose={() => setPasswordModalVisible(false)} />
      <DeleteAccountModal visible={deleteAccountModalVisible} onClose={() => setDeleteAccountModalVisible(false)} />
    </div>
  );
}
